import { useMemo } from "react";
import { Download, Trash2 } from "lucide-react";
import dayjs from "dayjs";

import { cn } from "@/utils/css";
import { BatchBadgeIcon } from "./BatchBadgeIcon";
import { BATCH_STATUS } from "./batchConstants";

const formatTimestamp = (date) => dayjs(date).format("MMM DD, HH:mm");

export const BatchPredictionRow = ({ prediction, onCancel, onDownload, onDelete }) => {
    const { fileName, extension, status, createdAt, lineCount, downloadable, warning } = prediction;
    const isProcessing = status === BATCH_STATUS.PROCESSING;
    const isFailed = status === BATCH_STATUS.FAILED || status === BATCH_STATUS.WARNING;

    const subline = useMemo(() => {
        if (isProcessing) return "Processing file...";
        if (isFailed) return null;
        if (typeof lineCount === "number") return `${lineCount} ${lineCount === 1 ? "line" : "lines"}`;
        return null;
    }, [isProcessing, isFailed, lineCount]);

    return (
        <div className="bg-cm-p-surface border border-cm-p-outline rounded-lg flex items-center gap-5 p-4">
            <div className="flex flex-1 min-w-0 items-center gap-3">
                <BatchBadgeIcon extension={extension} isProcessing={isProcessing} isFailed={isFailed} />
                <div className="flex flex-col justify-center min-w-0 flex-1">
                    <p className="text-cm-p-text-primary text-sm font-medium truncate">{fileName}</p>
                    {isFailed ? (
                        <p className="text-xs text-cm-p-on-container-red mt-0.5">Batch job failed.{warning ? ` ${warning}` : ""}</p>
                    ) : subline ? (
                        <p className="text-xs text-cm-p-text-secondary mt-0.5">{subline}</p>
                    ) : null}
                </div>
            </div>
            <p className="text-xs text-cm-p-text-secondary whitespace-nowrap">{formatTimestamp(createdAt)}</p>
            <div className="flex items-center gap-2 w-[170px] justify-end">
                {isProcessing ? (
                    <button
                        type="button"
                        onClick={() => onCancel(prediction)}
                        className="flex-1 h-10 px-4 rounded-md bg-cm-p-surface-hover text-cm-p-text-primary text-sm font-medium hover:bg-cm-p-surface-container-hover"
                    >
                        Cancel
                    </button>
                ) : (
                    <>
                        {downloadable && !isFailed ? (
                            <button
                                type="button"
                                onClick={() => onDownload(prediction)}
                                className="flex-1 h-10 rounded-md bg-cm-p-surface-hover hover:bg-cm-p-surface-container-hover text-cm-p-text-primary text-sm font-medium flex items-center justify-center gap-2 px-4"
                            >
                                <Download className="size-4" />
                                <span>Download</span>
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => onDelete(prediction)}
                            className={cn(
                                "h-10 rounded-md flex items-center justify-center",
                                isFailed
                                    ? "flex-1 bg-cm-p-surface-container hover:bg-cm-p-surface-container-hover text-cm-p-on-container-red"
                                    : "px-3 bg-cm-p-surface-hover hover:bg-cm-p-surface-container-hover text-cm-p-text-primary",
                            )}
                            aria-label="Delete"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
